import { addHours } from "date-fns";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NotFoundScreen from "../404";
import {
  CallCallStatus,
  CallParticipant,
  ParticipantRole,
} from "../api/graphql";
import { callIsWebinar } from "../lib/securityFeaturesUtils.ts";
import StreamTrackerProvider from "../lib/StreamTrackerProvider";
import CallSkeleton from "./CallSkeleton";
import EndedCallScreen from "./EndedCallScreen.tsx";
import { sendToParent } from "./iframeMessaging.ts";
import LiveCall from "./LiveCall";
import Lobby from "./Lobby";
import { IsolatedCall } from "./utils.tsx";

type CallPhase =
  "lobby" | "call" | "ended" | "terminated" | "connectedElsewhere";

type Props = {
  allowRating?: boolean;
  allowTextFeedback?: boolean;
  call: IsolatedCall;
  currentParticipant?: Pick<
    CallParticipant,
    "externalId" | "role" | "fullName" | "type"
  >;
  denied?: boolean;
  fromPath: string;
  standalone?: boolean;
};

export default function RoomScreen({
  allowRating,
  allowTextFeedback,
  call,
  currentParticipant,
  fromPath,
  standalone,
  denied,
}: Props) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<CallPhase>("lobby");
  const setPhaseAndNotify = useCallback(
    (newPhase: CallPhase) => {
      if (!call.id || newPhase === phase) return;
      setPhase(newPhase);
      if (!["lobby", "call", "connectedElsewhere"].includes(newPhase)) {
        sendToParent("CALL_ENDED", { callId: call.id, reason: newPhase });
      }
    },
    [call.id, phase]
  );
  const [beginMuted, setBeginMuted] = useState(false);
  const [beginHidden, setBeginHidden] = useState(false);
  const [hasPeerEverJoined, setHasPeerEverJoined] = useState(false);
  const [userLeftCall, setUserLeftCall] = useState(false);

  const callIsJoinable = !!call.url;

  // Sets the default muted state for webinar attendees
  useEffect(() => {
    const isWebinar = callIsWebinar(call);
    if (
      isWebinar &&
      currentParticipant &&
      currentParticipant.role !== ParticipantRole.WebinarHost
    ) {
      setBeginMuted(true);
    }
  }, [call, currentParticipant]);

  // collect feedback for an ended call, but only within an hour of the ending
  useEffect(() => {
    if (
      call.callStatus === CallCallStatus.Ended &&
      Date.now() < addHours(call.endedAt, 1).getTime()
    ) {
      setPhaseAndNotify("ended");
    }
  }, [call, setPhaseAndNotify]);

  // if the meeting is pending approval, scheduled, or live, do not 404,
  // let the user enter the lobby
  if (
    denied ||
    !currentParticipant ||
    (![CallCallStatus.Scheduled, CallCallStatus.Live].includes(
      call.callStatus
    ) &&
      phase !== "ended")
  ) {
    // phase is 'ended' if we already determined that the call ended today.
    // This is for all other statuses other than pending approval, scheduled,
    // and live, or if Ended/Terminated call is from before today.
    return <NotFoundScreen />;
  }

  let children: React.ReactNode;

  if (phase === "lobby") {
    children = (
      <Lobby
        currentParticipant={currentParticipant}
        standalone={standalone}
        fromPath={fromPath}
        title={call.title}
        onJoinButtonClick={
          callIsJoinable ? () => setPhaseAndNotify("call") : undefined
        }
        call={call}
        beginMuted={beginMuted}
        beginHidden={beginHidden}
        onToggleAudio={() => setBeginMuted((m) => !m)}
        onToggleVideo={() => setBeginHidden((p) => !p)}
      />
    );
  } else if (phase === "call") {
    children = (
      <Suspense fallback={<CallSkeleton />}>
        <LiveCall
          currentParticipant={currentParticipant}
          call={call}
          onLeave={() => {
            setUserLeftCall(true);
            sendToParent("USER_LEFT", {
              callId: call.id,
              userId: currentParticipant.externalId,
            });
            setPhase("ended");
          }}
          leaving={userLeftCall}
          beginHidden={beginHidden}
          beginMuted={beginMuted}
          alerts={false}
          onToggleAlerts={() => {}}
          onPeerJoined={() => {
            setHasPeerEverJoined(true);
          }}
          onTerminated={() => setPhaseAndNotify("terminated")}
          onConnectedElsewhere={() => setPhaseAndNotify("connectedElsewhere")}
          onAudioReady={() => {}}
          onEnded={() => setPhaseAndNotify("ended")}
        />
      </Suspense>
    );
  } else {
    children = (
      <EndedCallScreen
        currentParticipant={currentParticipant}
        call={call}
        title={userLeftCall ? t("You left the call") : t("This call has ended")}
        collectRating={!!allowRating && hasPeerEverJoined}
        terminated={phase === "terminated"}
        connectedElsewhere={phase === "connectedElsewhere"}
        rejoinCall={
          phase === "connectedElsewhere"
            ? () => setPhaseAndNotify("lobby")
            : undefined
        }
        allowTextFeedback={allowRating && allowTextFeedback}
      />
    );
  }

  return <StreamTrackerProvider>{children}</StreamTrackerProvider>;
}
